// Horizon — bespoke custom-letterform wordmark with a gold sunrise inside the first "O".
// Wide-tracked, chunky letterforms — feels like a luxury fashion-house lockup,
// not a generic SaaS logo. Each letter is hand-drawn from primitive shapes.
import { motion } from 'framer-motion';

export function HorizonLogo({ scale = 1, dark = false, animated = false, className = '' }) {
  const ink = dark ? '#FAF8F5' : '#1A1816';
  const gold = '#C9A96E';
  const Wrapper = animated ? motion.svg : 'svg';
  const wrapperProps = animated
    ? { initial: { opacity: 0, y: 8 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } }
    : {};
  const W = 360 * scale;
  const H = 84 * scale;
  return (
    <Wrapper
      viewBox="0 0 360 84"
      width={W}
      height={H}
      role="img"
      aria-label="Horizon"
      data-testid="horizon-logo"
      className={className}
      {...wrapperProps}
    >
      <defs>
        <linearGradient id="hd-sun" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor={gold} stopOpacity="0" />
          <stop offset="50%" stopColor={gold} stopOpacity="1" />
          <stop offset="100%" stopColor={gold} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Top hairline + gold center accent */}
      <line x1="0" y1="6" x2="360" y2="6" stroke={ink} strokeOpacity="0.35" strokeWidth="0.6" />
      <line x1="160" y1="0" x2="200" y2="0" stroke={gold} strokeWidth="3" />

      {/* HORIZON wordmark — wide-tracked, custom letterforms */}
      <g transform="translate(0, 26)" fill={ink}>
        {/* H */}
        <rect x="0" y="0" width="6.5" height="56" />
        <rect x="32" y="0" width="6.5" height="56" />
        <rect x="0" y="25" width="38.5" height="6.5" />
        {/* O — with sunrise inside */}
        <g transform="translate(53, 0)">
          <rect x="0" y="0" width="56" height="56" fill="none" stroke={ink} strokeWidth="6.5" />
          <line x1="6.5" y1="33" x2="49.5" y2="33" stroke={gold} strokeWidth="2" />
          <circle cx="28" cy="33" r="9" fill={gold} fillOpacity="0.95" />
          <line x1="6.5" y1="33" x2="49.5" y2="33" stroke="url(#hd-sun)" strokeWidth="2" />
        </g>
        {/* R — bowl rectangles + cleanly-connected diagonal leg */}
        <g transform="translate(124, 0)">
          <rect x="0" y="0" width="6.5" height="56" />
          <rect x="0" y="0" width="32" height="6.5" />
          <rect x="25.5" y="0" width="6.5" height="31.5" />
          <rect x="0" y="25" width="32" height="6.5" />
          <polygon points="12,31.5 18.5,31.5 32,56 25.5,56" />
        </g>
        {/* I */}
        <rect x="166" y="0" width="6.5" height="56" />
        {/* Z — top + bottom bars + clean diagonal stroke as polygon */}
        <g transform="translate(180, 0)">
          <rect x="0" y="0" width="40" height="6.5" />
          <rect x="0" y="49.5" width="40" height="6.5" />
          <polygon points="33,6.5 40,6.5 7,49.5 0,49.5" />
        </g>
        {/* O — plain */}
        <g transform="translate(232, 0)">
          <rect x="0" y="0" width="56" height="56" fill="none" stroke={ink} strokeWidth="6.5" />
        </g>
        {/* N */}
        <g transform="translate(304, 0)">
          <rect x="0" y="0" width="6.5" height="56" />
          <rect x="49.5" y="0" width="6.5" height="56" />
          <polygon points="0,0 6.5,0 56,56 49.5,56" />
        </g>
      </g>
    </Wrapper>
  );
}

/** Compact mark for tight spaces (favicon / OG, square crops) */
export function HorizonMark({ size = 56, dark = false, className = '' }) {
  const ink = dark ? '#FAF8F5' : '#1A1816';
  const gold = '#C9A96E';
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} role="img" aria-label="Horizon" className={className}>
      <rect x="2" y="2" width="60" height="60" fill="none" stroke={ink} strokeWidth="2" />
      <line x1="6" y1="32" x2="58" y2="32" stroke={gold} strokeWidth="1.5" />
      <circle cx="32" cy="32" r="9" fill={gold} fillOpacity="0.95" />
      <g fill={ink} transform="translate(20, 12)">
        <rect x="0" y="0" width="2.5" height="14" />
        <rect x="22" y="0" width="2.5" height="14" />
        <rect x="0" y="6" width="24.5" height="2.5" />
      </g>
    </svg>
  );
}
